import { queryGetMasterInfo } from '../api/queryGetMasterInfo';
import {
	allCarInfo,
	allDriverInfo,
	allOrganizationInfo,
	applicableData,
	coexistenceGetType,
	dialog,
	masterInfoLabel,
	sessionStorageKeyAuthorizationToken,
} from '../const/commonConst';
import { HandlerOutputType } from '../type/HandlerType';
import { MasterInfo } from '../type/MasterInfoType';
import { responseType } from '../type/ResponseType';
import { isResponseTypeError, isResponseTypeSessionNotExist } from '../utils/responseTypeChecker';
import { convertOrganizationInfoList, convertDriverInfoList, convertCarInfoList } from '../utils/masterInfoConverter';
import { nonNullable } from '../utils/nonNullable';
import { getSessionStorage } from '../utils/sessionStorageHandler';
import { redirectToLogin } from '../utils/redirectToLogin';

/**
 * Offseg・G500Liteのマスタ情報を取得する関数
 */
export const getMasterInfoHandler = async (
	getType: (typeof coexistenceGetType)[keyof typeof coexistenceGetType],
): Promise<HandlerOutputType<MasterInfo>> => {
	// 認証トークンを取得する
	const authorizationToken = getSessionStorage(sessionStorageKeyAuthorizationToken);

	if (!authorizationToken) {
		return [dialog.systemError, undefined];
	}

	const getMasterInfoInput = {
		authorizationToken: authorizationToken,
	};

	try {
		// Offseg・G500Liteのマスタ情報を取得する
		const [isApiError, getMasterInfoResult] = await queryGetMasterInfo(getType, getMasterInfoInput, authorizationToken);
		console.log(getMasterInfoResult);

		if (isApiError || !getMasterInfoResult || isResponseTypeError(getMasterInfoResult)) {
			return [dialog.apiError, undefined];
		}

		if (isResponseTypeSessionNotExist(getMasterInfoResult)) {
			redirectToLogin();
		}

		const notExistedList: string[] = [];

		// 取得結果の存在チェック
		if (getMasterInfoResult.getOffsegOrganizationInfo.responseType === responseType.notExistApiResponseData) {
			notExistedList.push(masterInfoLabel.offsegOrganization);
		}

		if (getMasterInfoResult.getOffsegCarInfo && getMasterInfoResult.getOffsegCarInfo.responseType === responseType.notExistApiResponseData) {
			notExistedList.push(masterInfoLabel.offsegCar);
		}

		if (getMasterInfoResult.getOffsegDriverInfo.responseType === responseType.notExistApiResponseData) {
			notExistedList.push(masterInfoLabel.offsegDriver);
		}

		if (getMasterInfoResult.getG500LiteOrganizationInfo.responseType === responseType.notExistApiResponseData) {
			notExistedList.push(masterInfoLabel.g500LiteOrganization);
		}

		if (getMasterInfoResult.getG500LiteCarInfo && getMasterInfoResult.getG500LiteCarInfo.responseType === responseType.notExistApiResponseData) {
			notExistedList.push(masterInfoLabel.g500LiteCar);
		}

		if (
			getMasterInfoResult.getG500LiteDriverInfo &&
			getMasterInfoResult.getG500LiteDriverInfo.responseType === responseType.notExistApiResponseData
		) {
			notExistedList.push(masterInfoLabel.g500LiteDriver);
		}

		if (notExistedList.length) {
			const customDialog = {
				isOpened: dialog.notExistMasterInfo.isOpened,
				title: dialog.notExistMasterInfo.title,
				text: dialog.notExistMasterInfo.text.replace(applicableData, notExistedList.join()), // カンマ区切で連結
			};
			return [customDialog, undefined];
		}

		const masterInfo: MasterInfo = {
			offsegOrganizationInfoList: [
				allOrganizationInfo,
				...convertOrganizationInfoList(getMasterInfoResult.getOffsegOrganizationInfo.organizationInfoList.filter(nonNullable)),
			],
			offsegCarInfoList: getMasterInfoResult.getOffsegCarInfo
				? [allCarInfo, ...convertCarInfoList(getMasterInfoResult.getOffsegCarInfo.carInfoList.filter(nonNullable))]
				: [],
			offsegDriverInfoList: [allDriverInfo, ...convertDriverInfoList(getMasterInfoResult.getOffsegDriverInfo.driverInfoList.filter(nonNullable))],
			g500LiteOrganizationInfoList: [
				allOrganizationInfo,
				...convertOrganizationInfoList(getMasterInfoResult.getG500LiteOrganizationInfo.organizationInfoList.filter(nonNullable)),
			],
			g500LiteCarInfoList: getMasterInfoResult.getG500LiteCarInfo
				? [allCarInfo, ...convertCarInfoList(getMasterInfoResult.getG500LiteCarInfo.carInfoList.filter(nonNullable))]
				: [],
			g500LiteDriverInfoList: getMasterInfoResult.getG500LiteDriverInfo
				? [allDriverInfo, ...convertDriverInfoList(getMasterInfoResult.getG500LiteDriverInfo.driverInfoList.filter(nonNullable))]
				: [],
		};
		console.log(masterInfo);

		return [dialog.success, masterInfo];
	} catch (err: unknown) {
		return [dialog.systemError, undefined];
	}
};
